import { GetQuotePayload } from 'api/insurance'
import { computeAccommodationOfferTotalsView } from 'checkout/lib/utils/accommodation/priceView'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import { findByName } from 'lib/insuranceCountries'
import moment from 'moment'

export type ValidCountry = GetQuotePayload['destination_countries'][0]
export type ValidRegion = GetQuotePayload['country_code']

const VALID_COUNTRIES = ['NZ', 'AU', 'SG', 'MY', 'TH', 'IN', 'HK']

export function isValidCountry(country: string): country is ValidCountry {
  const result = findByName(country)
  return !!result
}

export function isValidRegion(region: string): region is ValidRegion {
  return VALID_COUNTRIES.includes(region)
}

export function buildAccommodationQuotePayload(
  view: App.Checkout.AccommodationOfferView,
  insuranceCountries: string[],
  currencyCode: string,
  regionCode: string,
): Partial<GetQuotePayload> | null {
  const totalsView = computeAccommodationOfferTotalsView(view).data

  // Sanity checks
  if (
    !isValidRegion(regionCode) ||
    !insuranceCountries.length ||
    !insuranceCountries.every(isValidCountry) ||
    !view.startDate ||
    !totalsView.price
  ) {
    return null
  }

  const countries = insuranceCountries as ValidCountry[]

  const totalOccupants = view.occupancy.reduce((acc: App.Occupants, element: App.Occupants) => ({
    adults: acc.adults + (element?.adults ?? 0),
    children: (acc?.children ?? 0) + (element?.children ?? 0),
    infants: (acc?.infants ?? 0) + (element?.infants ?? 0),
  }), { adults: 0, children: 0, infants: 0 })

  return {
    destination_countries: countries,
    trip_start_date: moment.utc(view.startDate).format(ISO_DATE_FORMAT),
    trip_end_date: moment.utc(view.endDate).format(ISO_DATE_FORMAT),
    total_price: totalsView.price + (totalsView?.surcharge ?? 0),
    includingCovid: true,
    travellers_details: {
      number_of_adults: totalOccupants.adults,
      number_of_children: totalOccupants?.children ?? null,
      number_of_infants: totalOccupants?.infants ?? null,
      number_of_seniors: 0,
    },
  }
}

export function overrideInsuranceParams(
  params: GetQuotePayload,
  overrides: App.Checkout.InsuranceFetchParameters,
): GetQuotePayload {
  return {
    ...params,
    ...((overrides.numberOfSeniors && params.travellers_details?.number_of_adults) && {
      travellers_details: {
        ...params.travellers_details,
        number_of_adults: params.travellers_details.number_of_adults - overrides.numberOfSeniors,
        number_of_seniors: overrides.numberOfSeniors,
      },
    }),
    trip_start_date: overrides.startDate,
    trip_end_date: overrides.endDate,
    destination_countries: overrides.destinationCountries as ValidCountry[],
  }
}
